import { css } from '@emotion/react'
import React, { useState, useRef } from 'react'
import { CheckIcon, XIcon } from '@/react/componentAssets'
import { useAngularServices } from '@/react/components'
import { CurrentUserType } from '@/react/types/documentation'

interface EmailInputProps {
  onConfirm?: (email: string) => void
  onChange?: (email: string) => void
  width?: string | number
  isLoading?: boolean
  value?: string
}

const EmailInput = ({
  onConfirm,
  onChange,
  width = 430,
  isLoading = false,
  value = '',
}: EmailInputProps) => {
  const [email, setEmail] = useState(value)
  const { CurrentUser } = useAngularServices()
  const variableColor = (CurrentUser as CurrentUserType).getClientSettings()
    .web_primary_color
  const inputRef = useRef<HTMLInputElement>(null)

  // Update local state when value prop changes
  React.useEffect(() => {
    setEmail(value)
  }, [value])

  const handleClear = () => {
    setEmail('')
    onChange?.('')
    if (inputRef.current) {
      inputRef.current.value = ''
      inputRef.current.focus()
    }
  }

  const handleConfirm = () => {
    if (email.trim() && !isLoading) {
      onConfirm?.(email)
      inputRef.current?.focus()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleConfirm()
    }
  }

  const handleConfirmClick = () => {
    handleConfirm()
  }

  const base = getBaseStyle(variableColor, width)

  return (
    <div css={base}>
      <div
        css={email ? nonActiveInput : activeInput}
        className="base"
        id={'email-input-base'}
      >
        <input
          ref={inputRef}
          css={inputStyle}
          id={'email'}
          name={'email'}
          className="general-input-style"
          placeholder={'Enter email'}
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value)
            onChange?.(e.target.value)
          }}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
        />
      </div>
      {email && (
        <>
          <div className="email-row-buttons clear-button" onClick={handleClear}>
            <XIcon height={16} />
          </div>
          <div
            className={`email-row-buttons confirm-button ${
              isLoading ? 'loading' : ''
            }`}
            onClick={handleConfirmClick}
          >
            {isLoading ? '...' : <CheckIcon height={'12.3px'} width={16} />}
          </div>
        </>
      )}
    </div>
  )
}

const inputStyle = css({
  color: '#000',
  backgroundColor: '#FAFAFA',
  border: '1px solid #E6E6E6',
  fontSize: '12px',
  fontWeight: 600,
  letterSpacing: '-0.12px',
  padding: '4px 16px',
  outline: 'none',
  height: '28px',
})

function getBaseStyle(variableColor: string, width: string | number) {
  return css({
    display: 'flex',
    width: width,
    '.base': {
      display: 'flex',
      width: '100%',
      button: {
        height: '28px',
        borderWidth: 0,
        color: 'white',
        backgroundColor: variableColor,
      },
      '.clear': {
        display: 'flex',
        cursor: 'pointer',
        height: '28px',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#ED5565',
        width: '28px',
        span: {
          width: '12px',
          color: 'white',
        },
      },
    },
    '.email-row-buttons': {
      height: 28,
      width: 28,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: variableColor,
      cursor: 'pointer',
      flexShrink: 0,

      '&.loading': {
        opacity: 0.6,
        cursor: 'not-allowed',
      },
    },
    '.clear-button': {
      color: variableColor,
      ':hover': {
        backgroundColor: variableColor,
        color: 'white',
      },
    },
    '.confirm-button': {
      color: 'white',
      backgroundColor: variableColor,
      ':hover': {
        backgroundColor: 'transparent',
        color: variableColor,
      },
    },
  })
}

const activeInput = css({
  input: {
    width: '100%',
  },
})

const nonActiveInput = css({
  input: {
    width: '100%',
  },
})

export default EmailInput
